import { useState, useEffect, useContext, useMemo } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { Shield, User, Eye, Pencil, Trash2, Check, X, Search, AlertTriangle } from 'lucide-react';
import { ALL_PAGES } from '@/pages/settings/UserManagement';
import { canUserEdit, canUserDelete } from '@/utils/permissions';

// Pages that are actually consumed by code via canUserEdit/canUserDelete
// Used to detect stale/mismatched permission keys between code and Admin UI
const CODE_CONSUMED_PATHS = [
  '/dispatch/records',
  '/hr/calendar',
  '/hr/grievances',
  '/hr/leave',
  '/hr/master-roll',
  '/hr/salary',
  '/imports/claims',
  '/raw-material/consumption-stock',
  '/raw-material/dross-stock',
  '/raw-material/purchase-order',
  '/imports/grn',
  '/stores/consumable-stock',
  '/stores/grn',
  '/stores/payments',
  '/stores/purchase-order',
  '/stores/qa',
];

const ACCESS_BADGE = {
  none: { label: 'No Access', color: 'bg-gray-100 text-gray-600 border-gray-200' },
  view: { label: 'View Only', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  edit: { label: 'View + Edit', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  full: { label: 'Edit + Delete', color: 'bg-rose-50 text-rose-700 border-rose-200' },
};

function PermissionsAudit() {
  const { API, user: currentUser } = useContext(AppContext);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await axios.get(`${API}/auth/users`);
        setUsers(res.data || []);
      } catch (e) {
        console.error('Failed to fetch users', e);
        setFetchError(e?.response?.data?.detail || e?.message || 'Failed to fetch users');
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [API]);

  const selectedUser = useMemo(
    () => users.find((u) => u.id === selectedUserId || u._id === selectedUserId),
    [users, selectedUserId]
  );

  // Detect mismatched paths: keys in code but not in Admin UI config
  const configuredPaths = useMemo(() => {
    const set = new Set();
    ALL_PAGES.forEach((cat) => cat.pages.forEach((p) => set.add(p.path)));
    return set;
  }, []);

  const mismatches = useMemo(
    () => CODE_CONSUMED_PATHS.filter((p) => !configuredPaths.has(p)),
    [configuredPaths]
  );

  const filteredCategories = useMemo(() => {
    if (!searchTerm.trim()) return ALL_PAGES;
    const q = searchTerm.toLowerCase();
    return ALL_PAGES.map((cat) => ({
      ...cat,
      pages: cat.pages.filter(
        (p) => p.label.toLowerCase().includes(q) || p.path.toLowerCase().includes(q)
      ),
    })).filter((cat) => cat.pages.length > 0);
  }, [searchTerm]);

  // Summary counts for selected user
  const summary = useMemo(() => {
    if (!selectedUser) return null;
    const pa = selectedUser.page_access || {};
    const counts = { none: 0, view: 0, edit: 0, full: 0 };
    ALL_PAGES.forEach((cat) =>
      cat.pages.forEach((p) => {
        const lvl = selectedUser.role === 'Admin' ? 'full' : pa[p.path] || 'none';
        counts[lvl] = (counts[lvl] || 0) + 1;
      })
    );
    return counts;
  }, [selectedUser]);

  if (!currentUser || currentUser.role !== 'Admin') {
    return (
      <div className="p-8 text-center text-gray-500" data-testid="permissions-audit-forbidden">
        <Shield className="mx-auto mb-3 text-gray-400" size={40} />
        Admin access required.
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="permissions-audit-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="text-indigo-600" size={26} />
            Permissions Audit
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Preview exactly what any user sees — View, Edit, Delete buttons on every page.
          </p>
        </div>
      </div>

      {/* Mismatch warning */}
      {fetchError && (
        <div
          className="border border-rose-300 bg-rose-50 rounded-lg p-4 flex gap-3"
          data-testid="permissions-audit-fetch-error"
        >
          <AlertTriangle className="text-rose-600 shrink-0 mt-0.5" size={20} />
          <div className="text-sm">
            <p className="font-semibold text-rose-900">Could not load users</p>
            <p className="text-rose-800 mt-1">{fetchError}</p>
          </div>
        </div>
      )}

      {mismatches.length > 0 && (
        <div
          className="border border-amber-300 bg-amber-50 rounded-lg p-4 flex gap-3"
          data-testid="permissions-audit-mismatch-warning"
        >
          <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={20} />
          <div className="text-sm">
            <p className="font-semibold text-amber-900">
              {mismatches.length} path mismatch(es) between code and Admin UI
            </p>
            <p className="text-amber-800 mt-1">
              These paths are checked inside page components but are NOT present in User Management
              config. Users will never see Edit/Delete on these pages.
            </p>
            <ul className="mt-2 ml-4 list-disc text-amber-900 font-mono text-xs space-y-0.5">
              {mismatches.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* User selector */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select User to Audit
        </label>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
              disabled={loading}
              data-testid="permissions-audit-user-select"
            >
              <option value="">
                {loading ? 'Loading users...' : '— Select a user —'}
              </option>
              {users.map((u) => (
                <option key={u.id || u._id} value={u.id || u._id}>
                  {u.name} ({u.email}) · {u.role}
                </option>
              ))}
            </select>
          </div>
          <div className="relative sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Filter pages..."
              className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              data-testid="permissions-audit-search"
            />
          </div>
        </div>

        {/* Summary */}
        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5" data-testid="permissions-audit-summary">
            {Object.entries(summary).map(([lvl, count]) => (
              <div
                key={lvl}
                className={`px-4 py-3 rounded-lg border ${ACCESS_BADGE[lvl].color}`}
              >
                <div className="text-xs font-medium opacity-80">{ACCESS_BADGE[lvl].label}</div>
                <div className="text-2xl font-bold mt-0.5">{count}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Audit table */}
      {selectedUser ? (
        <div className="space-y-5" data-testid="permissions-audit-results">
          {filteredCategories.map((cat) => (
            <div key={cat.category} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-5 py-3 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-800">{cat.category}</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-5 py-2.5 font-medium">Page</th>
                      <th className="text-left px-5 py-2.5 font-medium">Path</th>
                      <th className="text-left px-5 py-2.5 font-medium">Access Level</th>
                      <th className="text-center px-3 py-2.5 font-medium w-16">View</th>
                      <th className="text-center px-3 py-2.5 font-medium w-16">Edit</th>
                      <th className="text-center px-3 py-2.5 font-medium w-16">Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cat.pages.map((p) => {
                      const lvl =
                        selectedUser.role === 'Admin'
                          ? 'full'
                          : (selectedUser.page_access || {})[p.path] || 'none';
                      const canView = lvl !== 'none';
                      const canEdit = canUserEdit(selectedUser, p.path);
                      const canDelete = canUserDelete(selectedUser, p.path);
                      return (
                        <tr
                          key={p.path}
                          className="border-t border-gray-100 hover:bg-gray-50/60"
                          data-testid={`permissions-row-${p.path}`}
                        >
                          <td className="px-5 py-2.5 text-gray-900">{p.label}</td>
                          <td className="px-5 py-2.5 text-gray-500 font-mono text-xs">{p.path}</td>
                          <td className="px-5 py-2.5">
                            <span
                              className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium border ${ACCESS_BADGE[lvl].color}`}
                            >
                              {ACCESS_BADGE[lvl].label}
                            </span>
                          </td>
                          <td className="text-center px-3 py-2.5">
                            <AccessIcon active={canView} Icon={Eye} />
                          </td>
                          <td className="text-center px-3 py-2.5">
                            <AccessIcon active={canEdit} Icon={Pencil} />
                          </td>
                          <td className="text-center px-3 py-2.5">
                            <AccessIcon active={canDelete} Icon={Trash2} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
          {filteredCategories.length === 0 && (
            <div className="text-center py-10 text-gray-500 bg-white border border-gray-200 rounded-lg">
              No pages match your search.
            </div>
          )}
        </div>
      ) : (
        !loading && (
          <div className="bg-white border-2 border-dashed border-gray-200 rounded-lg text-center py-14 text-gray-500">
            <User className="mx-auto mb-3 text-gray-300" size={36} />
            Select a user above to see their effective permissions.
          </div>
        )
      )}
    </div>
  );
}

function AccessIcon({ active, Icon }) {
  if (active) {
    return (
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200">
        <Check size={14} strokeWidth={3} />
      </span>
    );
  }
  return (
    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-50 text-gray-400 border border-gray-200">
      <X size={14} />
    </span>
  );
}

export default PermissionsAudit;
